import React, { useRef, useState } from "react";
import styles from "./WebFormBuilding.module.css";
import WebformFields from "./WebformFields";
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import ListPopover from "../../../../../../components/ListPopover/ListPopover";
import { useSelector } from "react-redux";
import { useAuthUser } from "react-auth-kit";
import { getSubDomainAccessStatus } from "../../../../../../components/RolePermissionAccess/RolePermissionAccess";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  getPrimaryFieldsByInstitute,
  resetWebformStatuses,
} from "../../service/webformSlice";
import { useDispatch } from "react-redux";
import WebformAddedFields from "./WebformAddedFields";
import {
  checkObjectsEqual,
  getCombinedAssignees,
} from "../../../../../../components/CommonFunctions/CommonFunctions";
import CancelBtn from "../../../../../../components/Buttons/CancelBtn";
import SaveBtn from "../../../../../../components/Buttons/SaveBtn";
import PreviewFormPopup from "../PreviewFormPopup/PreviewFormPopup";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useNavigate } from "react-router-dom";
import CancelPopup from "../../../../../../components/CommonPopups/CancelPopup";

function WebformBuilder(props: any) {
  const {
    add,
    GetWebformForInstitute,
    getFormByInstituteData,
    CreateWebform,
    UpdateWebform,
    UpdateWebformSuccess,
    UpdateWebformLoading,
    CreateWebformSuccess,
    CreateWebformId,
    CreateWebformLoading,
  } = props;
  const auth = useAuthUser();
  const authUser = auth();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const branchList = useSelector((state: any) => state.branch.branchList);
  const { userById } = useSelector((state: any) => state.userDetails);
  const { getPrimaryFieldsData } = useSelector((state: any) => state.webform);
  const employees = useSelector((state: any) => state.staff.staffList);

  const activeInstitute = JSON.parse(localStorage.getItem("ACTIVE_INSTITUTE")!);

  const hasAllBranchAccess = getSubDomainAccessStatus(
    "GLOBAL_PERMISSIONS",
    "BRANCH_ACCESS",
    "ALL_BRANCHES",
    userById?.rolePermission
  );

  const hasNurturePay =
    activeInstitute &&
    Array.isArray(activeInstitute.integrationsOpted) &&
    activeInstitute?.integrationsOpted?.includes("NURTURE_PAY_EASEBUZZ");

  const filteredBranchList = hasAllBranchAccess
    ? branchList
    : branchList?.filter(
        (branch: any) => branch.id === authUser?.institute?.[0]?.branchId
      );

  const branchFieldRef = useRef<HTMLInputElement | null>(null);
  const formTypeFieldRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<any>(null);
  const assigneeFieldRef = useRef<HTMLInputElement | null>(null);

  const [formType, setFormType] = useState<string>("");
  const [branchAnchorEl, setBranchAnchorEl] = useState(null);
  const openBranch = Boolean(branchAnchorEl);
  const [searchBranch, setSearchBranch] = useState<string>("");
  const [selectedBranch, setSelectedBranch] = useState<any>(null);
  const [leadCheck, setLeadCheck] = useState<boolean>(false);
  const [assigneeAnchor, setAssigneeAnchor] = useState(null);
  const [searchAssignee, setSearchAssignee] = useState<string>("");
  const openAssignee = Boolean(assigneeAnchor);
  const [formTypeAnchorEl, setFormTypeAnchorEl] = useState(null);
  const openFormType = Boolean(formTypeAnchorEl);
  const [selectedAssignees, setSelectedAssignees] = useState<any>(null);
  const [paymentCheck, setPaymentCheck] = useState<boolean>(false);
  const [amount, setAmount] = useState<number>(0);
  const [amountError, setAmountError] = useState<string>("");
  const [formName, setFormName] = useState<string>("");
  const [btnName, setBtnName] = useState<string>("submit");
  const [assigneeError, setAssigneeError] = useState<string>("");
  const [branchError, setBranchError] = useState<string>("");

  const [customFields, setCustomFields] = useState<any[]>([]);
  const [defaultFields, setDefaultFields] = useState<any[]>([]);
  const [fields, setFields] = useState<any[]>([]);
  const [draggedField, setDraggedField] = useState<any>(null);

  const [draggedFieldIndex, setDraggedFieldIndex] = useState<number | null>(
    null
  );
  const [dragActive, setDragActive] = useState<boolean>(false);
  const [formPreview, setFormPreview] = useState<boolean>(false);
  const [isPublish, setIsPublish] = useState<boolean>(false);
  const [cancelModal, setCancelModal] = useState<boolean>(false);

  const filteredDefaultFields = defaultFields?.filter(
    (field) =>
      !fields?.some((additional) => additional.id === field.id) &&
      !(formType === "BRANCH_SPECIFIC" && field.fieldType === "BRANCH")
  );
  const filteredCustomFields = customFields?.filter(
    (field) => !fields?.some((additional) => additional.id === field.id)
  );

  const filteredEmployees =
    leadCheck && (formType === "UNIVERSAL" || formType === "")
      ? employees?.filter((branch: any) => branch.allBranchAccess === true)
      : formType === "BRANCH_SPECIFIC" && selectedBranch && leadCheck
      ? getCombinedAssignees(employees, selectedBranch?.id)
      : [];

  const handleFormTypeSelect = (emp: any) => {
    setFormType(emp.name);
    handleCloseFormTypePopover();
    setSelectedBranch(null);
    setSelectedAssignees(null);
    if (emp.name === "BRANCH_SPECIFIC") {
      const branchFieldExists = fields?.some(
        (field) => field.fieldType === "BRANCH"
      );
      if (branchFieldExists) {
        const updatedFields = fields?.filter(
          (field) => field.fieldType !== "BRANCH"
        );
        const reIndexedFields = updatedFields?.map((field, index) => ({
          ...field,
          index: index + 1,
        }));
        setFields(reIndexedFields);
      }
    }
  };

  const handleOpenBranchPopover = (event: any) => {
    setBranchAnchorEl(event.currentTarget);
    const tempElement = document.createElement("input");
    document.body.appendChild(tempElement);
    tempElement.focus();
    document.body.removeChild(tempElement);
  };

  const handleOpenFormTypePopover = (event: any) => {
    setFormTypeAnchorEl(event.currentTarget);
    const tempElement = document.createElement("input");
    document.body.appendChild(tempElement);
    tempElement.focus();
    document.body.removeChild(tempElement);
  };

  const handleCloseBranchPopover = () => {
    setBranchAnchorEl(null);
  };

  const handleCloseFormTypePopover = () => {
    setFormTypeAnchorEl(null);
  };

  const handleBranchSearch = (event: any) => {
    setSearchBranch(event.target.value);
  };

  const handleBranchSelect = (emp: any) => {
    setSelectedBranch(emp);
    handleCloseBranchPopover();
    setSelectedAssignees(null);
    setAssigneeError("");
    setBranchError("");
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLeadCheck(event.target.checked);
    setSelectedAssignees(null);
    setAssigneeError("");
    setPaymentCheck(false);
    setAmountError("");
    setAmount(0);
    if (event.target.checked) {
      const fieldTypesToAdd = ["COURSE", "BRANCH", "BATCH"];
      const existingFieldTypes = fields.map((field) => field.fieldType);
      const newFields = filteredDefaultFields
        .filter(
          (field) =>
            fieldTypesToAdd.includes(field.fieldType) &&
            !existingFieldTypes.includes(field.fieldType)
        )
        .map((field, index) => ({
          ...field,
          required: field.fieldType === "BATCH" ? false : true,
          index: fields.length + index + 1,
        }));
      console.log("abee", newFields);

      setFields([...fields, ...newFields]);
    } else {
      if (paymentCheck) {
        setBtnName("Submit");
      }
    }
  };

  const handleOpenPopover = (event: any) => {
    setAssigneeAnchor(event.currentTarget);
    const tempElement = document.createElement("input");
    document.body.appendChild(tempElement);
    tempElement.focus();
    document.body.removeChild(tempElement);
  };

  const handleClosePopover = () => {
    setAssigneeAnchor(null);
  };

  const handleSearch = (event: any) => {
    setSearchAssignee(event.target.value);
  };
  const handleAssigneeSelect = (emp: any) => {
    setSelectedAssignees(emp);
    handleClosePopover();
    setAssigneeError("");
  };

  const handlePaymentCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentCheck(event.target.checked);
    setAmountError("");
    setAmount(0);
    if (event.target.checked) {
      setBtnName("Save and Proceed to pay");
    } else {
      setBtnName("Submit");
    }
  };

  const handleAmountChange = (event: any) => {
    const enteredAmount = parseFloat(event.target.value);
    setAmount(enteredAmount);
    console.log("amount", event.target.value);
    if (enteredAmount < 0) {
      setAmountError("Amount Received cannot be negative");
    } else if (enteredAmount === 0) {
      setAmountError("Please enter a number greater than zero");
    } else if (enteredAmount > 0) {
      setAmountError("");
    } else {
      setAmountError("Please enter a number greater than zero");
    }
  };

  function handleOnDrag(e: any, field: any) {
    setDraggedField(field);
  }
  console.log("enteredAmount", amount);

  console.log("draggedField", draggedField);
  function handleOnDrop(e: any) {
    e.preventDefault();
    console.log("fieldExist");

    if (draggedField) {
      let fieldExist: any;

      fieldExist = fields?.filter(
        (item: any) => item?.fieldName === draggedField?.fieldName
      );
      console.log("fieldExist", fieldExist);

      if (fieldExist?.length > 0) {
        setDraggedField(null);
      } else {
        const lastIndex =
          fields?.length > 0 ? fields[fields.length - 1].index || 0 : -1;

        const updatedDraggedField = {
          ...draggedField,
          index: fields?.length === 0 ? 1 : lastIndex + 1,
        };
        setFields([...fields, updatedDraggedField]);
        setDraggedField(null);
      }
    }
  }

  function handleOnDragFields(e: any, field: any, index: number) {
    setDraggedField(field);
    setDraggedFieldIndex(index);
    setDragActive(true);
  }

  function handleOnDragEnter(e: any, field: any, index: number) {
    if (draggedField !== null && draggedFieldIndex !== null) {
      const updatedFields = [...fields];
      updatedFields.splice(draggedFieldIndex, 1);
      updatedFields.splice(index, 0, draggedField);
      const updatedFieldsWithIndex = updatedFields?.map(
        (item, index: number) => ({
          ...item,
          index: index + 1,
        })
      );
      setFields(updatedFieldsWithIndex);
      setDraggedField(null);
      setDraggedFieldIndex(null);
    }
  }

  function handleOnDragOver(e: any) {
    e.preventDefault();
    if (dragActive) {
      const y = e.clientY;
      const containerRect = containerRef.current.getBoundingClientRect();
      const scrollSpeed = 10;
      let scrollDirection = 0;

      if (y < containerRect.top + 100) {
        scrollDirection = -1;
      } else if (y > containerRect.bottom - 100) {
        scrollDirection = 1;
      }

      if (scrollDirection !== 0) {
        containerRef.current.scrollTop += scrollDirection * scrollSpeed;
      }
    }
  }

  const isObjectsEqual = checkObjectsEqual(
    {},
    {}
    //   { formName: formName, fields: additionalFields },
    //   { formName: leadFormById?.formName, fields: oldAdditionalFields }
  );

  const cancelBtnClick = () => {
    if (isObjectsEqual) {
      navigate(`/app/Inbound/Webforms`);
      localStorage.removeItem("createWebFormName");
    } else {
      setCancelModal(true);
    }
  };

  const closeCancelModal = () => {
    setCancelModal(false);
  };

  const cancelYesBtn = () => {
    setCancelModal(false);
    navigate(`/app/Inbound/Webforms`);
    localStorage.removeItem("createWebFormName");
  };

  const handleNext = () => {
    if (formType === "BRANCH_SPECIFIC" && selectedBranch === null) {
      setBranchError("Please fill required field");
      return;
    }
    if (leadCheck && selectedAssignees === null) {
      setAssigneeError("Please fill required field");
      return;
    }
    if (leadCheck && paymentCheck && (amount <= 0 || isNaN(amount))) {
      setAmountError("Please fill required field");
      return;
    }
    setFormPreview(true);
    setIsPublish(true);
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();

    try {
      if (formName !== "") {
        const updatedFields = fields?.map((item: any, index: number) => ({
          isDeletable: true,
          fieldPlaceholder: item?.fieldPlaceholder || "",
          default: item?.default || "",
          fieldName: item?.fieldName || "",
          DBModel: item?.DBModel || "",
          options: item?.options || [],
          isCustom: item?.isCustom || false,
          index: index + 1,
          loadDatafromDB: item?.loadDatafromDB || false,
          fieldType: item?.fieldType || "text",
          required: item?.required || false,
          type: item?.type,
          id: item?.id || "",
        }));

        const body = {
          id: add ? "" : getFormByInstituteData?.id,
          formName: formName,
          instituteId: activeInstitute?.id,
          fields: updatedFields,
          buttonName: btnName,
          type: formType,
          isCollectPayment: paymentCheck,
          amount: paymentCheck ? amount : 0,
          webformType: leadCheck ? "LEAD_CREATION" : "CONTACT_CREATION",
          status: "PUBLISHED",
          assignedToName:
            selectedAssignees?.firstName + " " + selectedAssignees?.lastName,
          assignedToId: selectedAssignees?.id,
          branchId: formType === "BRANCH_SPECIFIC" ? selectedBranch?.id : "",
          isCreateLead: leadCheck,
        };

        add
          ? CreateWebform({
              ...body,
              createdById: authUser?.institute[0]?.id,
              createdByName: authUser?.firstName + " " + authUser?.lastName,
            })
          : UpdateWebform({
              ...body,
              createdById: getFormByInstituteData?.createdById,
              createdByName: getFormByInstituteData?.createdByName,
              updatedById: authUser?.institute[0]?.id,
              updatedByName: authUser?.firstName + " " + authUser?.lastName,
            });
      }
    } catch (error) {}
  };

  const hasInvalidOptions = fields?.some(
    (item: any) =>
      (item.fieldType === "radio" ||
        item.fieldType === "checkbox" ||
        item.fieldType === "select" ||
        item.fieldType === "COURSE" ||
        item.fieldType === "BRANCH" ||
        item.fieldType === "BATCH") &&
      item.options?.length === 0
  );

  React.useEffect(() => {
    const lastEvaluatedKey = "";
    const limit = 30;
    const lastEvaluatedSortKey = "";

    if (activeInstitute) {
      dispatch<any>(
        getPrimaryFieldsByInstitute({
          id: activeInstitute?.id,
          lastEvaluatedKey,
          limit,
          lastEvaluatedSortKey,
        })
      );
    }
  }, [authUser, dispatch]);

  React.useEffect(() => {
    if (getPrimaryFieldsData?.customParameterData?.fields?.length > 0) {
      const updatedFields =
        getPrimaryFieldsData?.customParameterData?.fields?.map(
          (item: any, index: number) => ({
            fieldType: item?.field?.fieldType,
            fieldName: item?.field?.fieldName,
            fieldPlaceholder: item?.field?.fieldPlaceholder,
            options: item?.field?.options,
            default: item?.field?.default,
            required: item?.field?.required,
            index: index + 1,
            loadDatafromDB: item?.field?.loadDatafromDB,
            DBModel: item?.field?.DBModel,
            isCustom: item?.field?.isCustom,
            id: item?.id,
            type: item?.field?.type,
          })
        );

      setCustomFields(updatedFields);
    }
    if (getPrimaryFieldsData?.defaultParameter?.length > 0) {
      const updatedDefaultFields = getPrimaryFieldsData?.defaultParameter
        ?.slice()
        ?.sort((a: any, b: any) => (a.id > b.id ? 1 : -1))
        ?.reverse()
        ?.map((item: any, index: number) => ({
          fieldType: item?.field?.fieldType,
          fieldName: item?.field?.fieldName,
          fieldPlaceholder: item?.field?.fieldPlaceholder,
          options: item?.field?.options,
          default: item?.field?.default,
          required: item?.field?.required,
          index: index + 1,
          loadDatafromDB: item?.field?.loadDatafromDB,
          DBModel: item?.field?.DBModel,
          isCustom: false,
          id: item?.id,
          type: item?.field?.type,
        }))
        ?.filter(
          (field: any) =>
            field.fieldType === "BATCH" ||
            field.fieldType === "BRANCH" ||
            field.fieldType === "COURSE"
        )
        ?.sort((a: any, b: any) => {
          const order: Record<string, number> = {
            COURSE: 1,
            BRANCH: 2,
            BATCH: 3,
          };
          return (
            (order[a.fieldType as keyof typeof order] || 0) -
            (order[b.fieldType as keyof typeof order] || 0)
          );
        });

      setDefaultFields(updatedDefaultFields);
    }
  }, [getPrimaryFieldsData]);

  React.useEffect(() => {
    if (!add) {
      const pathname = window.location.pathname;
      const formId = pathname.substring(pathname.lastIndexOf("/") + 1);
      const userFromLocalStorage = authUser?.institute[0];
      if (userFromLocalStorage && formId) {
        GetWebformForInstitute(userFromLocalStorage?.instituteId, formId);
      }
    }
  }, [GetWebformForInstitute, add]);

  React.useEffect(() => {
    if (add) {
      const savedFormName = localStorage.getItem("createWebFormName") || "";
      setFormName(savedFormName);
      if (!hasAllBranchAccess) {
        setFormType("BRANCH_SPECIFIC");
      } else {
        setFormType("UNIVERSAL");
      }
      const newFields = getPrimaryFieldsData?.defaultParameter
        ?.slice()
        // ?.sort((a: any, b: any) => (a.id > b.id ? 1 : -1))
        ?.reverse()
        ?.filter(
          (item: any) =>
            item?.field.fieldType !== "BATCH" &&
            item?.field.fieldType !== "BRANCH" &&
            item?.field.fieldType !== "COURSE"
        )
        ?.map((item: any, index: number) => ({
          fieldType: item?.field?.fieldType,
          fieldName: item?.field?.fieldName,
          fieldPlaceholder: item?.field?.fieldPlaceholder,
          options: item?.field?.options,
          default: item?.field?.default,
          required:
            item?.field?.fieldName === "email" ? false : item?.field?.required,
          index: index + 1,
          loadDatafromDB: item?.field?.loadDatafromDB,
          DBModel: item?.field?.DBModel,
          isCustom: false,
          id: item?.id,
          type: item?.field?.type,
        }));

      setFields(newFields);
    } else {
      setFormName(getFormByInstituteData?.formName);
      setFormType(getFormByInstituteData?.type);
      if (getFormByInstituteData?.type === "BRANCH_SPECIFIC") {
        const branch = branchList?.find(
          (item: any) => item?.id === getFormByInstituteData?.branchId
        );
        setSelectedBranch(branch);
      }
      setBtnName(getFormByInstituteData?.buttonName);

      setLeadCheck(
        getFormByInstituteData?.webformType === "LEAD_CREATION" ? true : false
      );
      if (getFormByInstituteData?.webformType === "LEAD_CREATION") {
        const selectedEmployee = employees?.find(
          (item: any) => item?.id === getFormByInstituteData?.assignedToId
        );
        if (selectedEmployee) {
          setSelectedAssignees(selectedEmployee);
        }
        setPaymentCheck(getFormByInstituteData?.isCollectPayment);
        if (getFormByInstituteData?.isCollectPayment) {
          setAmount(getFormByInstituteData?.amount);
        }
      }

      const newFields = getFormByInstituteData?.fields?.map(
        (item: any, index: number) => ({
          fieldType: item?.fieldType,
          fieldName: item?.fieldName,
          fieldPlaceholder: item?.fieldPlaceholder,
          options: item?.options,
          default: item?.default,
          required: item?.required,
          index: index + 1,
          loadDatafromDB: item?.loadDatafromDB,
          DBModel: item?.DBModel,
          isCustom: false,
          id: item?.id,
          type: item?.type,
        })
      );

      setFields(newFields);
    }
  }, [
    getFormByInstituteData,
    add,
    employees,
    branchList,
    getPrimaryFieldsData,
  ]);
  React.useEffect(() => {
    if (
      (UpdateWebformSuccess !== false && UpdateWebformSuccess !== false) ||
      (CreateWebformSuccess !== false &&
        CreateWebformSuccess !== false &&
        CreateWebformId)
    ) {
      dispatch(resetWebformStatuses());
      navigate(
        `/app/Inbound/Webforms?webformTab=forms&webformSharePopup=${
          CreateWebformId ? CreateWebformId?.id : getFormByInstituteData?.id
        }`
      );
    }
  }, [UpdateWebformSuccess, CreateWebformSuccess, CreateWebformId]);

  console.log(
    "getPrimaryFieldsData",
    // getPrimaryFieldsData?.defaultParameter,
    getFormByInstituteData,
    fields
  );
  return (
    <div className={styles.webformBuilderWrap}>
      <div className={styles.webformBuilderLeft}>
        <WebformFields
          defaultFields={filteredDefaultFields}
          customFields={filteredCustomFields}
          handleOnDrag={handleOnDrag}
        />
      </div>
      <div className={styles.webformBuilderWrapRight}>
        <div
          className={styles.webformBuilderFormNameWrap}
          style={{ alignItems: branchError ? "flex-start" : "center" }}
        >
          <div>{formName}</div>
          <div className={styles.webformBuilderFormNameWrapRight}>
            <div
              className={styles.webformBuilderTypeWrap}
              style={{ alignItems: branchError ? "flex-start" : "center" }}
            >
              <div
                style={{
                  color: "rgba(102, 112, 133, 1)",
                  marginRight: "10px",
                }}
              >
                Type
              </div>

              <TextField
                onClick={handleOpenFormTypePopover}
                value={
                  formType ? formType?.toLowerCase()?.replace(/_/g, " ") : ""
                }
                variant="outlined"
                size="small"
                placeholder="Select"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <ArrowDropDownIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root input": {
                    textTransform: "capitalize",
                    color: "#667085",
                    fontSize: "14px",
                    fontWeight: "500",
                  },
                  width: "180px",
                }}
                inputRef={formTypeFieldRef}
              />
              <ListPopover
                openState={openFormType}
                anchorState={formTypeAnchorEl}
                handleClosePopover={handleCloseFormTypePopover}
                handleListSearch={handleBranchSearch}
                popupList={
                  hasAllBranchAccess
                    ? [
                        { id: 0, name: "UNIVERSAL" },
                        { id: 1, name: "BRANCH_SPECIFIC" },
                      ]
                    : [{ id: 1, name: "BRANCH_SPECIFIC" }]
                }
                handleItemSelect={handleFormTypeSelect}
                listType={"FORMTYPE"}
              />
            </div>
            {formType === "BRANCH_SPECIFIC" && (
              <div style={{ display: "flex", alignItems: "center" }}>
                <TextField
                  onClick={handleOpenBranchPopover}
                  value={selectedBranch ? selectedBranch?.branchName : ""}
                  variant="outlined"
                  size="small"
                  placeholder="Branch"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <ArrowDropDownIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root input": {
                      textTransform: "capitalize",
                      color: "#667085",
                      fontSize: "14px",
                      fontWeight: "500",
                    },
                    width: "180px",
                  }}
                  inputRef={branchFieldRef}
                  error={branchError ? true : false}
                  helperText={branchError}
                />
                <ListPopover
                  openState={openBranch}
                  anchorState={branchAnchorEl}
                  handleClosePopover={handleCloseBranchPopover}
                  handleListSearch={handleBranchSearch}
                  popupList={filteredBranchList?.filter((item: any) =>
                    item?.branchName
                      .toLowerCase()
                      .includes(searchBranch?.toLowerCase())
                  )}
                  handleItemSelect={handleBranchSelect}
                  listType={"BRANCH"}
                />
              </div>
            )}
          </div>
        </div>
        <div className={styles.leadCheckWrap}>
          <FormControlLabel
            control={
              <Checkbox
                sx={{
                  strokeWidth: 1,
                  color: "#D0D5DD",
                }}
                checked={leadCheck}
                onChange={handleCheckboxChange}
              />
            }
            label={
              <Typography
                style={{
                  fontSize: "12px",
                  fontWeight: "400",
                  color: "#667085",
                  lineHeight: "20px",
                }}
              >
                Directly create leads from the responses
              </Typography>
            }
          />
          {leadCheck && (
            <>
              <TextField
                onClick={handleOpenPopover}
                value={
                  selectedAssignees
                    ? `${selectedAssignees?.firstName} ${selectedAssignees?.lastName}`
                    : ""
                }
                variant="outlined"
                size="small"
                placeholder="Select Assignee"
                inputRef={assigneeFieldRef}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <ArrowDropDownIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: "220px",
                  width: "220px",
                  "& .MuiOutlinedInput-root input": {
                    textTransform: "capitalize",
                  },
                  "& .MuiOutlinedInput-root ": {
                    textTransform: "capitalize",
                  },
                  "& .MuiOutlinedInput-root fieldset": {
                    padding: "8px 14px",
                  },
                }}
                error={assigneeError ? true : false}
                helperText={assigneeError}
              />
              <ListPopover
                openState={openAssignee}
                anchorState={assigneeAnchor}
                handleClosePopover={handleClosePopover}
                handleListSearch={handleSearch}
                popupList={filteredEmployees
                  ?.filter(
                    (emp: any) =>
                      emp.status === "ACTIVE" &&
                      emp.firstName
                        .toLowerCase()
                        .includes(searchAssignee.toLowerCase())
                  )
                  ?.filter((user: any) => {
                    return user?.leadCardAccess === true;
                  })}
                handleItemSelect={handleAssigneeSelect}
                listType={"ASSIGNEE"}
              />
            </>
          )}
        </div>
        {leadCheck && hasNurturePay && (
          <div className={styles.paymentWrap}>
            <FormControlLabel
              control={
                <Checkbox
                  sx={{
                    strokeWidth: 1,
                    color: "#D0D5DD",
                    padding: "0 9px 0 9px",
                  }}
                  checked={paymentCheck}
                  onChange={handlePaymentCheck}
                />
              }
              label={
                <Typography
                  style={{
                    fontSize: "12px",
                    fontWeight: "400",
                    color: "#667085",
                    lineHeight: "20px",
                  }}
                >
                  Collect Payment
                </Typography>
              }
            />
            {paymentCheck && (
              <TextField
                type="number"
                id="amountReceived"
                required
                placeholder="₹"
                variant="outlined"
                onChange={handleAmountChange}
                error={!!amountError}
                helperText={amountError || ""}
                inputProps={{ min: "0" }}
                sx={{
                  width: "200px",
                  "& .MuiInputLabel-root": {
                    color: "#B1B1B1",
                    fontWeight: "300",
                    fontSize: "12px",
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "10px 16px",
                  },
                }}
                value={amount}
              />
            )}
          </div>
        )}

        <WebformAddedFields
          handleOnDrop={handleOnDrop}
          handleOnDragOver={handleOnDragOver}
          handleOnDragFields={handleOnDragFields}
          handleOnDragEnter={handleOnDragEnter}
          fields={fields}
          paymentCheck={paymentCheck}
          leadCheck={leadCheck}
          setFields={setFields}
          btnName={btnName}
          setBtnName={setBtnName}
        />

        <div className={styles.webformBtnsWrap}>
          {hasInvalidOptions && (
            <FormHelperText error>
              Please fill answers for fields which requires answers.
            </FormHelperText>
          )}
          <Button
            component="label"
            sx={{
              textTransform: "capitalize",
              fontWeight: "500",
              display: "flex",
            }}
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => {
              setFormPreview(!formPreview);
              setIsPublish(false);
            }}
          >
            Preview Form
          </Button>

          {formPreview && (
            <PreviewFormPopup
              showPreviewPopup={formPreview}
              onClose={() => setFormPreview(false)}
              isComingFromNextButton={isPublish}
              setIscomingFromNextButton={setIsPublish}
              rightSectionFields={fields}
              handleUpdateForm={handleSubmit}
              updatedButtonName={btnName}
              UpdateWebformLoading={UpdateWebformLoading}
              CreateWebformLoading={CreateWebformLoading}
            />
          )}
          <div className={styles.webformBtnsSplit}>
            <CancelBtn onClick={cancelBtnClick} />
            <SaveBtn
              buttonText="Next"
              onClick={handleNext}
              disabled={formType === ""}
            />
          </div>
          <CancelPopup
            open={cancelModal}
            onClose={closeCancelModal}
            noBtnClick={closeCancelModal}
            yesBtnClick={cancelYesBtn}
          />
        </div>
      </div>
    </div>
  );
}

export default WebformBuilder;
